// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames/bind';
import { FormValidator } from '../../../utils/form';
import Arrow from '../../../elements/Arrow/Arrow';
import { Default, Mobile } from '../../../utils/responsive';
import Slider from 'react-slick';
import Modal from '../../../elements/Modal/Modal';
import { Helmet } from 'react-helmet';
import analytics from '../../../analytics';
import FreqQuestions from '../../../elements/FreqQuestions/FreqQuestions';


import style from './LandingTiendas.scss';
import tiendas_landing from '../../../assets/img/tiendas/tiendas_landing.png';
import formStyle from '../../../assets/styles/form.scss';
import inputStyle from '../../../assets/styles/input.scss';
import buttonStyle from '../../../assets/styles/button.scss';
import icons from '../../../assets/styles/icons.scss';
import store_web from '../../../assets/img/tiendas/carouselPropositions/store_web.png';
import wallet_dollar_card from '../../../assets/img/tiendas/carouselPropositions/wallet_dollar_card.png';
import truck_location from '../../../assets/img/tiendas/carouselPropositions/truck_location.png';
import registration_success from '../../../assets/img/tiendas/registration_success.png';
import qempo_tiendas_logo from '../../../assets/img/tiendas/qempo_tiendas_logo.png';
import completed from '../../../assets/img/tiendas/roadmap/completed.png';
import in_progress from '../../../assets/img/tiendas/roadmap/in_progress.png';
import future from '../../../assets/img/tiendas/roadmap/future.png';

import type { TiendaRequest } from '../api';
import type { HTTPError } from '../../../error';
import type { LocaleState } from '../../locale/duck';
import type { Element } from "react";
import type { FormValidationResult } from '../../../utils/form';
import type { Question } from '../../../elements/FreqQuestions/FreqQuestions';

type Props = {
  +locale: LocaleState;
  +registerStore: (tiendaRequest: TiendaRequest) => void;
  +isRegistering?: boolean;
  +registrationSuccess?: boolean;
  +registrationError?: HTTPError;
  +resetState: () => void;
};

type State = {
  name: {
    value: string;
    errors?: string[];
  };
  shopName: {
    value: string;
    errors?: string[];
  };
  phone: {
    value: string;
    errors?: string[];
  };
  email: {
    value: string;
    errors?: string[];
  };
  isOpenModal?: boolean;
  lightningLeftArrow: boolean;
  lightningRightArrow: boolean;
};

const messages = defineMessages({
  title: {
    id: 'tiendas.landing.title',
  },
  description: {
    id: 'tiendas.landing.description',
  },
})

const propositions = [
  {
    value: 'tiendas.landing.carousel.one',
    image: store_web,
  },
  {
    value: 'tiendas.landing.carousel.two',
    image: wallet_dollar_card,
  },
  {
    US: {
      value: 'tiendas.landing.carousel.three.PE',
    },
    PE: {
      value: 'tiendas.landing.carousel.three.PE',
    },
    CO: {
      value: 'tiendas.landing.carousel.three.CO',
    },
    image: truck_location,
  },
];

const questionsIntlMap = {
  first: {
    title: 'tiendas.landing.faq.first.title',
    content: 'tiendas.landing.faq.first.content',
  },
  second: {
    title: 'tiendas.landing.faq.second.title',
    content: 'tiendas.landing.faq.second.content',
  },
  third: {
    title: 'tiendas.landing.faq.third.title',
    content: 'tiendas.landing.faq.third.content',
  },
};

const roadmapIntlMap = {
  completed: [
    { id: 'tiendas.landing.roadmap.completed.first.title' },
    { id: 'tiendas.landing.roadmap.completed.second.title' },
    { id: 'tiendas.landing.roadmap.completed.third.title' },
  ],
  inProgress: [
    { id: 'tiendas.landing.roadmap.inProgress.first.title' },
    { id: 'tiendas.landing.roadmap.inProgress.second.title' },
    { id: 'tiendas.landing.roadmap.inProgress.third.title' },
  ],
  future: [
    { id: 'tiendas.landing.roadmap.future.first.title' },
    { id: 'tiendas.landing.roadmap.future.second.title' },
    { id: 'tiendas.landing.roadmap.future.third.title' },
    { id: 'tiendas.landing.roadmap.future.fourth.title' },
    { id: 'tiendas.landing.roadmap.future.fifth.title' },
    { id: 'tiendas.landing.roadmap.future.sixth.title' },
    { id: 'tiendas.landing.roadmap.future.seventh.title' },
  ],
}

export default class LandingTiendas extends Component<Props, State> {
  formValidator: FormValidator;
  state = {
    name: { value: '' },
    shopName: { value: '' },
    phone: { value: '' },
    email: { value: '' },
    isOpenModal: false,
    lightningRightArrow: false,
    lightningLeftArrow: true,
  };

  constructor(props: Props) {
    super();
    this.formValidator = new FormValidator({
      name: {
        required: 'required',
      },
      shopName: {
        required: 'required',
      },
      phone: {
        required: 'required',
      },
      email: {
        required: 'required',
        emailFormat: 'emailFormat',
      },
    });
  }

  componentDidUpdate(prevProps: Props): * {
    const { isRegistering, registrationSuccess } = prevProps;
    if (isRegistering && !this.props.isRegistering &&
      !registrationSuccess && this.props.registrationSuccess) {
      this.setState({ isOpenModal: true });
    }
  }

  componentWillUnmount(): * {
    const { resetState } = this.props;
    resetState();
  }

  handleOpenModal = () => this.setState({ isOpenModal: true });

  handleCloseModal = () => this.setState({ isOpenModal: false });

  handleOnChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    this.setState({ [name]: { value } });
  };

  handleSubmit = (e: SyntheticInputEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { registerStore, locale } = this.props;
    const { shopName, phone, name, email } = this.state;
    const errors = this.formValidator.validateForm({
      shopName: shopName.value,
      phone: phone.value,
      name: name.value,
      email: email.value,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) => {
        this.setState((prevState: State) => ({
          [error.field]: {
            ...prevState[error.field],
            errors: error.errors,
          },
        }));
      });
    } else {
      analytics.fireCustomEvent('Qempo Shop Register Lead', {
        category: 'Qempo Shop',
        action: 'Register Lead',
        user_type: 'purchaser',
        user_referrer: window.localStorage.getItem('userReferrer') || '',
        landing_url: window.localStorage.getItem('landingUrl') || '',
        domain_country: locale.domainCountry,
      });
      const tiendaRequest: TiendaRequest = {
        shopName: shopName.value,
        phone: phone.value,
        name: name.value,
        email: email.value,
      }
      registerStore(tiendaRequest);
    }
  };


  handleArrowStyle = (current: number, lastIndex: number) => {
    const lightningLeftArrow = current === 0;
    const lightningRightArrow = current === lastIndex;
    this.setState({ lightningLeftArrow, lightningRightArrow });
  };

  buildItemBox = (): Element<'article'>[] =>
    propositions.map((proposition: { [key: string]: any }, index: number) => {
      const { locale } = this.props;
      let textValue = proposition.value
      if (index === 2) textValue = proposition[locale.domainCountry || 'PE'].value;
      return (
        <article key={index}
                 className={style.propositionWrap}>
          <div className={style.proposition}>
            <div className={style.imgWrap}>
              <img src={proposition.image} alt={`proposition-${index}`}/>
            </div>
            <FormattedMessage id={textValue}>{
              txt => <p>{txt}</p>
            }</FormattedMessage>
          </div>
        </article>);
    });

  buildBulletPoint = (column: string) =>
    roadmapIntlMap[column].map((feature: { [id: string]: string }, index: number) =>
      <div key={index} className={style.feature}>
        <FormattedMessage id={feature.id}>{
          txt => <p className={style.bullet}>&#8226;&nbsp;&nbsp;&nbsp;{txt}</p>
        }</FormattedMessage>
      </div>);

  render() {
    const { isRegistering } = this.props;
    const { email, name, phone, shopName, isOpenModal, lightningLeftArrow, lightningRightArrow } = this.state;
    const emailError = email.errors && FormValidator.buildErrorLabel(email.errors, {
      emailFormat: 'tiendas.landing.email.error.emailFormat',
      required: 'tiendas.landing.email.error.required',
    });
    const nameError = name.errors && FormValidator.buildErrorLabel(name.errors, {
      required: 'tiendas.landing.name.error.required',
    });
    const phoneError = phone.errors && FormValidator.buildErrorLabel(phone.errors, {
      required: 'tiendas.landing.phone.error.required',
    });
    const shopNameError = shopName.errors && FormValidator.buildErrorLabel(shopName.errors, {
      required: 'tiendas.landing.shopName.error.required',
    });

    const title = this.context.intl.formatMessage(messages.title);
    const description = this.context.intl.formatMessage(messages.description);

    const questions: Question[] = Object.keys(questionsIntlMap).map((key: string) => ({
      labelIntl: questionsIntlMap[key].title,
      contentIntl: questionsIntlMap[key].content,
    }));

    const helmet =
      <Helmet meta={[
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { name: 'twitter:title', content: title },
        { name: 'twitter:twitter:description', content: description },
      ]}>
        <title>{title}</title>
        <style type="text/css">{`
          body {
            padding-top: 0;
          }`}</style>
      </Helmet>;

    const carouselItems = this.buildItemBox();
    const options = {
      arrows: false,
      infinite: false,
      slidesToScroll: 1,
      slidesToShow: 3,
      speed: 500,
      dotsClass: classNames('slick-dots', style.dots),
      nextArrow: <Arrow icon={icons.iconChevronRightMedium}
                        positionClass={style.arrowRight}
                        visible={lightningRightArrow}/>,
      prevArrow: <Arrow icon={icons.iconChevronLeftMedium}
                        positionClass={style.arrowLeft}
                        visible={lightningLeftArrow}/>,
    };
    const mobileOptions = {
      ...options,
      slidesToShow: 1,
      arrows: true,
      draggable: true,
      dots: true,
    };
    return (
      <Fragment>
        {helmet}
        <div className={style.imgWrap}>
          <img className={style.logo}
               src={qempo_tiendas_logo}
               alt="Tiendas logo"/>
        </div>
        <div className={style.landingWrap}>
          <div className={style.topWrap}>
            <div className={style.leftSide}>
              <FormattedMessage id="tiendas.landing.main.title">{
                txt => <h1 className={style.title}>{txt}</h1>
              }</FormattedMessage>
              <FormattedMessage id="tiendas.landing.subtitle">{
                txt => <h3 className={style.subtitle}>{txt}</h3>
              }</FormattedMessage>
              <img className={style.topImg}
                   src={tiendas_landing}
                   alt="tiendas landing"/>
            </div>
            <div className={style.rightSide}>
              <FormattedMessage id="tiendas.landing.formTitle">{
                txt => <h2 className={style.formTitle}>{txt}:</h2>
              }</FormattedMessage>
              <form className={classNames(style.formWrap, formStyle.formWrap)}
                    onSubmit={this.handleSubmit}>
                <div className={formStyle.row}>
                  <FormattedMessage id="tiendas.landing.name.label">{
                    txt => <label className={formStyle.label}>{txt}:</label>
                  }</FormattedMessage>
                  <input type="text"
                         className={classNames(inputStyle.input, {
                           [inputStyle.lightBlue]: name.value,
                           [inputStyle.red]: name.errors && !!name.errors,
                         })}
                         onChange={this.handleOnChange}
                         disabled={isRegistering}
                         name="name"
                         value={name.value}/>
                  {nameError}
                </div>
                <div className={formStyle.row}>
                  <FormattedMessage id="tiendas.landing.shopName.label">{
                    txt => <label className={formStyle.label}>{txt}:</label>
                  }</FormattedMessage>
                  <input type="text"
                         className={classNames(inputStyle.input, {
                           [inputStyle.lightBlue]: shopName.value,
                           [inputStyle.red]: shopName.errors && !!shopName.errors,
                         })}
                         onChange={this.handleOnChange}
                         disabled={isRegistering}
                         name="shopName"
                         value={shopName.value}/>
                  {shopNameError}
                </div>
                <div className={formStyle.row}>
                  <FormattedMessage id="tiendas.landing.phone.label">{
                    txt => <label className={formStyle.label}>{txt}:</label>
                  }</FormattedMessage>
                  <input type="text"
                         className={classNames(inputStyle.input, {
                           [inputStyle.lightBlue]: phone.value,
                           [inputStyle.red]: phone.errors && !!phone.errors,
                         })}
                         onChange={this.handleOnChange}
                         disabled={isRegistering}
                         name="phone"
                         value={phone.value}/>
                  {phoneError}
                </div>
                <div className={formStyle.row}>
                  <FormattedMessage id="tiendas.landing.email.label">{
                    txt => <label className={formStyle.label}>{txt}:</label>
                  }</FormattedMessage>
                  <input type="text"
                         className={classNames(inputStyle.input, {
                           [inputStyle.lightBlue]: email.value,
                           [inputStyle.red]: email.errors && !!email.errors,
                         })}
                         onChange={this.handleOnChange}
                         disabled={isRegistering}
                         name="email"
                         value={email.value}/>
                  {emailError}
                </div>
                <FormattedMessage id="tiendas.landing.button.label">{
                  txt => <button type="submit"
                                 disabled={isRegistering}
                                 className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                  >{txt}</button>
                }</FormattedMessage>
              </form>
            </div>
          </div>
          <div className={style.carouselMainWrap}>
            <FormattedMessage id="tiendas.landing.carouselTitle">{
              txt => <h2 className={style.carouselTitle}>{txt}</h2>
            }</FormattedMessage>
            <div className={style.carouselWrap}>
              <Mobile>
                <Slider {...mobileOptions}>
                  {carouselItems}
                </Slider>
              </Mobile>
              <Default>
                <Slider {...options}>
                  {carouselItems}
                </Slider>
              </Default>
            </div>
          </div>
        </div>
        <div className={style.faqWrap}>
          <FreqQuestions titleIntl="tiendas.landing.faq.title"
                         questions={questions}
                         customClassName={style.faqCustomClass}
                         textOpenClassName={style.textOpenClassName}/>
        </div>
        <div className={style.roadmapWrap}>
          <FormattedMessage id="tiendas.landing.roadmap.title">{
            txt => <h1 className={style.title}>{txt}</h1>
          }</FormattedMessage>
          <FormattedMessage id="tiendas.landing.roadmap.subtitle">{
            txt => <p className={style.subtitle}>{txt}</p>
          }</FormattedMessage>
          <div className={style.roadmapColumns}>
            <div className={style.completed}>
              <FormattedMessage id="tiendas.landing.roadmap.completed.title">{
                txt => <h2 className={style.title}><img src={completed} alt="completed"/>{txt}</h2>
              }</FormattedMessage>
              {this.buildBulletPoint('completed')}
            </div>
            <div className={style.inProgress}>
              <FormattedMessage id="tiendas.landing.roadmap.inProgress.title">{
                txt => <h2 className={style.title}><img src={in_progress} alt="in_progress"/>{txt}</h2>
              }</FormattedMessage>
              {this.buildBulletPoint('inProgress')}
            </div>
            <div className={style.future}>
              <FormattedMessage id="tiendas.landing.roadmap.future.title">{
                txt => <h2 className={style.title}><img src={future} alt="future"/>{txt}</h2>
              }</FormattedMessage>
              {this.buildBulletPoint('future')}
            </div>
          </div>
        </div>
        <Modal handleClose={this.handleCloseModal}
               open={isOpenModal}
               width={768}>
          <FormattedMessage id="tiendas.landing.modal.title">{
            txt => <h2 className={style.modalTitle}>{txt}</h2>
          }</FormattedMessage>
          <img className={style.modalImg}
               src={registration_success}
               alt="welcome"/>
        </Modal>
      </Fragment>
    )
  }
}

LandingTiendas.propTypes = {
  registerStore: PropTypes.func.isRequired,
  isRegistering: PropTypes.bool,
  registrationSuccess: PropTypes.bool,
  registrationError: PropTypes.object,
  resetState: PropTypes.func.isRequired,
};

LandingTiendas.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/tiendas/components/LandingTiendas.jsx